/**
 * Vendor Financial Redirect Component (Issue #1173 Enhancement)
 *
 * Provides backward compatibility for old URLs without fair ID.
 * Redirects /vendor/revenue, /vendor/expenses, /vendor/roi-tracker
 * to /vendor/fairs/:fairId/... using the first confirmed reservation.
 *
 * If user has no confirmed reservations, shows helpful message.
 */

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2, AlertCircle, Calendar } from 'lucide-react';
import { authApi, vendorApi } from '../services/api';
import { VendorReservation } from '../types';
import { extractApiError } from '../utils/errorUtils';

interface VendorFinancialRedirectProps {
  basePath: 'revenue' | 'expenses' | 'roi-tracker';
}

export const VendorFinancialRedirect: React.FC<VendorFinancialRedirectProps> = ({ basePath }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [vendorId, setVendorId] = useState<number | null>(null);

  useEffect(() => {
    const loadAndRedirect = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get current user to find vendor ID
        const user = await authApi.getCurrentUser();
        setVendorId(user.id);

        // Get user's vendors and their reservations
        const vendors = await vendorApi.getMyVendors();

        if (vendors.length === 0) {
          setError('You need to create a vendor profile first.');
          setLoading(false);
          return;
        }

        // Get reservations for the first vendor
        const reservations: VendorReservation[] = await vendorApi.getVendorReservations(vendors[0].id);
        const confirmedReservations = reservations.filter(r => r.status === 'confirmed');

        if (confirmedReservations.length === 0) {
          setError('You need at least one confirmed fair reservation to track finances.');
          setLoading(false);
          return;
        }

        // Redirect to first confirmed reservation's fair
        const fairId = confirmedReservations[0].fair_id;
        setRedirectUrl(`/vendor/fairs/${fairId}/${basePath}`);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        setError(apiError.message);
        console.error('Failed to redirect:', apiError);
      } finally {
        setLoading(false);
      }
    };

    loadAndRedirect();
  }, [basePath]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <Loader2 className="w-12 h-12 animate-spin text-secondary" />
          <p className="text-gray-600">Loading your fairs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Setup Required</h2>
                <p className="text-gray-700 mb-4">{error}</p>
                <div className="flex gap-3">
                  {!vendorId && (
                    <a
                      href="/vendor/profile/new"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                    >
                      Create Vendor Profile
                    </a>
                  )}
                  <a
                    href="/"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Calendar className="w-4 h-4" />
                    Find Fairs
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (redirectUrl) {
    return <Navigate to={redirectUrl} replace />;
  }

  return null;
};
